import {
  rtkApi,
  GET_STUDENTS,
  GET_STUDENT,
  GET_APPLICANTS,
  GET_USER_NOTES,
  GET_USER_ACTIVITIES,
  GET_APPLICATIONS,
  GET_APPLICATIONS_COUNT,
  GET_APPLICATION,
  GET_ACCOUNT_DELETIONS,
} from '@/shared/api/rtkApi';
import { LocalStorageService } from '@/shared/services/localStorage.service';
import {
  StudentsResponse,
  StudentsParams,
  StudentsPickerParams,
  StudentsPickerResponse,
  ToggleUserBlockResponse,
  BulkToggleUserBlockRequest,
  BulkToggleUserBlockResponse,
  UpdateStudentStateRequest,
  UpdateStudentStateResponse,
  StudentAccountCardResponse,
  UserActivitiesParams,
  UserActivitiesResponse,
  DeleteStudentResponse,
  DeleteUserResponse,
  CreateStudentNoteRequest,
  CreateStudentNoteResponse,
  UpdateStudentNoteRequest,
  UpdateStudentNoteResponse,
  UserNotesResponse,
  DeleteStudentNoteResponse,
  StudentDetailNote,
  UpdateStudentPersonalDataResponse,
  CreateStudentRequest,
  CreateStudentResponse,
  ManualEnrollStudentRequest,
  ManualEnrollStudentResponse,
  StudentAccountsParams,
  StudentAccountsResponse,
  AccountDeletionsParams,
  AccountDeletionsResponse,
  AccountDeletionProblemsCountResponse,
  RetryAccountDeletionResponse,
  ResendAccountDeletionNoticeResponse,
} from './types';

export const studentsApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    createStudent: build.mutation<CreateStudentResponse, CreateStudentRequest>({
      query: (body) => ({
        url: '/admin/students',
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_STUDENTS],
    }),
    manualEnrollStudent: build.mutation<
      ManualEnrollStudentResponse,
      { userId: number; body: ManualEnrollStudentRequest }
    >({
      query: ({ userId, body }) => ({
        url: `/admin/students/${userId}/manual-enroll`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_STUDENTS, GET_APPLICANTS, GET_APPLICATIONS, GET_APPLICATIONS_COUNT],
    }),
    getStudents: build.query<StudentsResponse, StudentsParams>({
      query: (params) => ({
        url: '/admin/students',
        params,
      }),
      providesTags: [GET_STUDENTS],
    }),
    getStudentAccounts: build.query<StudentAccountsResponse, StudentAccountsParams>({
      query: (params) => ({
        url: '/admin/students/accounts',
        params,
      }),
      providesTags: [GET_STUDENTS, GET_USER_NOTES],
    }),
    getAccountDeletions: build.query<AccountDeletionsResponse, AccountDeletionsParams>({
      query: (params) => ({
        url: '/admin/students/account-deletions',
        params,
      }),
      providesTags: [GET_ACCOUNT_DELETIONS],
    }),
    /** Счётчик меню и вкладки: заявки, которым нужен человек. Без доступа к странице — нули. */
    getAccountDeletionProblemsCount: build.query<AccountDeletionProblemsCountResponse, void>({
      query: () => ({
        url: '/admin/students/account-deletions/problems/count',
      }),
      providesTags: [GET_ACCOUNT_DELETIONS],
    }),
    retryAccountDeletion: build.mutation<RetryAccountDeletionResponse, { requestId: string }>({
      query: ({ requestId }) => ({
        url: `/admin/students/account-deletions/${requestId}/retry`,
        method: 'POST',
      }),
      invalidatesTags: [GET_ACCOUNT_DELETIONS],
    }),
    resendAccountDeletionNotice: build.mutation<
      ResendAccountDeletionNoticeResponse,
      { requestId: string }
    >({
      query: ({ requestId }) => ({
        url: `/admin/students/account-deletions/${requestId}/resend-notice`,
        method: 'POST',
      }),
      invalidatesTags: [GET_ACCOUNT_DELETIONS],
    }),
    getStudentsPicker: build.query<StudentsPickerResponse, StudentsPickerParams | void>({
      query: (params) => ({
        url: '/admin/students/picker',
        params: params || undefined,
      }),
      providesTags: [GET_STUDENTS],
    }),
    /** Переключает блокировку любой учётной записи (студента или сотрудника). */
    toggleUserBlock: build.mutation<ToggleUserBlockResponse, { userId: number }>({
      query: ({ userId }) => ({
        url: `/admin/users/${userId}/block`,
        method: 'PUT',
      }),
      invalidatesTags: [GET_STUDENTS, GET_STUDENT, GET_APPLICANTS, GET_USER_ACTIVITIES],
    }),
    /** Переключает блокировку каждому из `userIds`; кого изменить нельзя — в `data.errors`. */
    bulkToggleUserBlock: build.mutation<BulkToggleUserBlockResponse, BulkToggleUserBlockRequest>({
      query: (body) => ({
        url: '/admin/users/bulk-block',
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_STUDENTS, GET_STUDENT, GET_APPLICANTS, GET_USER_ACTIVITIES],
    }),
    deleteStudent: build.mutation<DeleteStudentResponse, { enrollmentId: number }>({
      query: ({ enrollmentId }) => ({
        url: `/admin/students/${enrollmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [GET_STUDENTS, GET_APPLICANTS],
    }),
    deleteUser: build.mutation<DeleteUserResponse, { userId: number }>({
      query: ({ userId }) => ({
        url: `/admin/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        GET_STUDENTS,
        GET_STUDENT,
        GET_APPLICANTS,
        GET_APPLICATIONS,
        GET_APPLICATIONS_COUNT,
        GET_APPLICATION,
        GET_USER_NOTES,
      ],
    }),
    updateStudentState: build.mutation<
      UpdateStudentStateResponse,
      { enrollmentId: number; body: UpdateStudentStateRequest }
    >({
      query: ({ enrollmentId, body }) => ({
        url: `/admin/students/${enrollmentId}/state`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [GET_STUDENTS, GET_STUDENT, GET_USER_ACTIVITIES],
    }),
    /**
     * Карточка учётной записи студента: все заявки и обучения человека с правами сотрудника.
     * Модалки заявки и обучения берут из неё свою запись (`mapStudentAccountCardToDetail`,
     * `mapStudentAccountCardToApplicationDetail`), поэтому одна карточка — один запрос и один кэш.
     * Тег заявки — чтобы правка заявки обновляла и её.
     */
    getStudentAccountCard: build.query<StudentAccountCardResponse, { userId: number }>({
      query: ({ userId }) => ({ url: `/admin/students/accounts/${userId}` }),
      keepUnusedDataFor: 0,
      providesTags: (result, error, { userId }) => [
        { type: GET_STUDENT, id: userId },
        { type: GET_APPLICATION, id: userId },
      ],
    }),
    /** Журнал действий человека, новые первыми. */
    getUserActivities: build.query<UserActivitiesResponse, UserActivitiesParams>({
      query: ({ userId, page, pageSize }) => ({
        url: `/admin/users/${userId}/activities`,
        params: { page, pageSize },
      }),
      providesTags: (result, error, { userId }) => [{ type: GET_USER_ACTIVITIES, id: userId }],
    }),
    createStudentNote: build.mutation<
      CreateStudentNoteResponse,
      { userId: number; body: CreateStudentNoteRequest }
    >({
      query: ({ userId, body }) => ({
        url: `/admin/users/${userId}/notes`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { userId }) => [
        GET_STUDENTS,
        { type: GET_USER_NOTES, id: userId },
      ],
    }),
    updateStudentNote: build.mutation<
      UpdateStudentNoteResponse,
      { userId: number; noteId: number; body: UpdateStudentNoteRequest }
    >({
      query: ({ userId, noteId, body }) => ({
        url: `/admin/users/${userId}/notes/${noteId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { userId }) => [
        GET_STUDENTS,
        { type: GET_USER_NOTES, id: userId },
      ],
    }),
    getUserNotes: build.query<StudentDetailNote[], { userId: number }>({
      query: ({ userId }) => ({
        url: `/admin/users/${userId}/notes`,
      }),
      transformResponse: (response: UserNotesResponse) => {
        const payload = response?.data;
        if (Array.isArray(payload)) return payload;
        if (payload && Array.isArray(payload.notes)) return payload.notes;
        return [];
      },
      providesTags: (result, error, { userId }) => [{ type: GET_USER_NOTES, id: userId }],
    }),
    deleteStudentNote: build.mutation<
      DeleteStudentNoteResponse,
      { userId: number; noteId: number }
    >({
      query: ({ userId, noteId }) => ({
        url: `/admin/users/${userId}/notes/${noteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { userId }) => [
        GET_STUDENTS,
        { type: GET_USER_NOTES, id: userId },
      ],
    }),
    updateStudentPersonalData: build.mutation<
      UpdateStudentPersonalDataResponse,
      { userId: number; enrollmentId?: number; body: FormData }
    >({
      queryFn: async ({ userId, enrollmentId, body }) => {
        const accessToken = LocalStorageService.getAccessToken();
        const headers = new Headers();
        if (accessToken) {
          headers.append('Authorization', `Bearer ${accessToken}`);
        }
        headers.append('Accept', 'application/json');

        try {
          const apiUrl = typeof __API__ === 'string' ? __API__ : import.meta.env.VITE_API_URL || '';
          const query = enrollmentId != null ? `?enrollmentId=${enrollmentId}` : '';
          const response = await fetch(
            `${apiUrl}/admin/students/${userId}/personal-data${query}`,
            {
              method: 'PUT',
              headers,
              credentials: 'include',
              body,
            },
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
            return {
              error: {
                status: response.status,
                data: errorData,
              },
            };
          }

          const data = await response.json();
          return { data };
        } catch (error: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error.message || 'Network error',
            },
          };
        }
      },
      invalidatesTags: [GET_STUDENTS, GET_STUDENT, GET_APPLICANTS],
    }),
  }),
});

export const {
  useCreateStudentMutation,
  useManualEnrollStudentMutation,
  useGetStudentsQuery,
  useLazyGetStudentsQuery,
  useGetStudentAccountsQuery,
  useGetAccountDeletionsQuery,
  useGetAccountDeletionProblemsCountQuery,
  useRetryAccountDeletionMutation,
  useResendAccountDeletionNoticeMutation,
  useGetStudentsPickerQuery,
  useToggleUserBlockMutation,
  useBulkToggleUserBlockMutation,
  useDeleteStudentMutation,
  useDeleteUserMutation,
  useUpdateStudentStateMutation,
  useGetStudentAccountCardQuery,
  useGetUserActivitiesQuery,
  useCreateStudentNoteMutation,
  useUpdateStudentNoteMutation,
  useGetUserNotesQuery,
  useDeleteStudentNoteMutation,
  useUpdateStudentPersonalDataMutation,
} = studentsApi;
